import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { ArrowRightLeft, Users, CheckCircle, XCircle, MessageSquare, ChevronDown, PlusCircle, Wifi, WifiOff, SendHorizontal, X } from 'lucide-react';
// import ExportButton from '../../sharedComponents/ExportButton';
import { useNotification } from '../../sharedComponents/Notifications';
import Drawer from '../../sharedComponents/Drawer';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import {
    submissionsByDeskService,
    primaFacieService,
    submissionService,
    Submission,
    DeskItem,
    SubmissionStatus,
    CategoryMemberDropdownItem,
    categoryAdminService,
    Category,
    userService,
    isAuthorMoveDecision,
    statusForAuthorMoveDecision,
} from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';
import { communicationService, InternalMessage } from '../services/communicationService';
import signalRService, { ConnectionStatus } from '../../../services/signalRService';
import { formatRoleLabel } from '../../../utils/roleLabel';


// ─────────────────────────────────────────────────────────────────────────────

function SubmissionsByDesk() {
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const navigate = useNavigate();

    const isCategoryAdmin = user?.role === 'thematicarealeader';
    const isTagChairman = user?.roleId === 5;
    const isDESS = user?.roleId === 9 || user?.role === 'dess';
    const isDESSOrTagChairman = isTagChairman || isDESS;

    // Desk owner label for the page header (e.g. "Submissions by Tag Chairman's Desk")
    const deskRoleLabel = isTagChairman ? 'Tag Chairman'
        : isDESS ? 'DESS'
        : isCategoryAdmin ? 'Thematic Area Leader'
        : formatRoleLabel(user?.displayRole);

    const location = useLocation();
    const { refreshUnread } = useAuth();

    const isReadyForReviewerAssignment = (row: Submission) =>
        row.completedPrimaFacie === true && row.primaFacieDecision === 'Recommended';

    // ── Category resolution ───────────────────────────────────────────────────
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);

    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state — search only
    const [search, setSearch] = useState('');

    // Move submission modal state
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [moveSubmission, setMoveSubmission] = useState<Submission | null>(null);
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);
    const [selectedDeskId, setSelectedDeskId] = useState<number | ''>('');
    const [moveComment, setMoveComment] = useState('');
    const [isMoving, setIsMoving] = useState(false);

    // Prima Facie panel modal state
    const [isPrimaFacieAssignOpen, setIsPrimaFacieAssignOpen] = useState(false);
    const [primaFacieTargetSubmission, setPrimaFacieTargetSubmission] = useState<Submission | null>(null);
    const [categoryMembers, setCategoryMembers] = useState<CategoryMemberDropdownItem[]>([]);
    const [fetchingMembers, setFetchingMembers] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState<(string | number)[]>([]);
    const [isAssigningMembers, setIsAssigningMembers] = useState(false);

    // Change status modal state
    const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
    const [statusTarget, setStatusTarget] = useState<Submission | null>(null);
    const [newStatus, setNewStatus] = useState<number | ''>('');
    const [statusComment, setStatusComment] = useState('');
    
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // ── Internal Discussion Chat State (DESS / TAG Chairman only) ─────────────
    const [chatSubmission, setChatSubmission] = useState<Submission | null>(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<InternalMessage[]>([]);
    const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [composeMessage, setComposeMessage] = useState<string>('');
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.Disconnected);
    const [unreadSubmissionIds, setUnreadSubmissionIds] = useState<Set<string>>(new Set());

    const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
    const [roleUsers, setRoleUsers] = useState<{ id: string; name: string }[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data) {
                    setCategories(res.data);
                    if (isCategoryAdmin) {
                        if (res.data.length > 0) {
                            const id = res.data[0].id;
                            setCategoryAdminCategoryId(id);
                        } else {
                            setCategoryAdminCategoryId(0);
                            setLoading(false);
                        }
                    } else {
                        setCategoryAdminCategoryId(0);
                    }
                } else {
                    if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
                }
            } catch {
                if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, [isCategoryAdmin]);

    // ── Fetch submissions ──────────────────────────────────────────────────────

    useEffect(() => {
        if (!initialized) return;
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;
        if (isCategoryAdmin && categoryAdminCategoryId === 0) return;

        const handler = setTimeout(() => {
            fetchSubmissions();
        }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, search, categoryAdminCategoryId]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const response = await submissionsByDeskService.getSubmissionsByDesk({
                pageNumber: currentPage,
                pageSize,
                sortBy: 'SubmittedAt',
                sortDescending: true,
                search,
                categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : undefined,
            });

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch submissions',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching submissions',
            });
            } finally {
            setLoading(false);
        }
    };

    // ── Fetch desk list when move modal opens ──────────────────────────────────

    const openMoveModal = async (submission: Submission) => {
        setMoveSubmission(submission);
        setSelectedDeskId('');
        setMoveComment('');
        setIsMoveModalOpen(true);
        setDeskListLoading(true);
        try {
            const response = await submissionsByDeskService.getDeskList();
            setDeskList(response.isSuccess && response.data ? response.data : []);
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'Failed to load desk list',
            });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeMoveModal = () => {
        setIsMoveModalOpen(false);
        setMoveSubmission(null);
        setSelectedDeskId('');
        setMoveComment('');
    };

    const handleMoveSubmission = async () => {
        if (!moveSubmission || selectedDeskId === '') return;

        setIsMoving(true);
        try {
            const response = await submissionsByDeskService.moveSubmission({
                submissionId: moveSubmission.id,
                comment: moveComment,
                toWorkflowStage: selectedDeskId as number,
            });

            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'Submission moved successfully',
                });
                closeMoveModal();
                fetchSubmissions();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to move submission',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while moving the submission',
            });
            } finally {
            setIsMoving(false);
        }
    };

    // ── Prima Facie panel handlers ─────────────────────────────────────────────

    const openPrimaFacieAssignModal = async (submission: Submission) => {
        setPrimaFacieTargetSubmission(submission);
        setSelectedMemberIds([]);
        setIsPrimaFacieAssignOpen(true);
        setFetchingMembers(true);
        try {
            const response = await primaFacieService.getCategoryMemberDropdown(submission.categoryId);
            if (response.isSuccess && response.data) {
                setCategoryMembers(response.data);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to load thematic area members',
                });
                setCategoryMembers([]);
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while loading members',
            });
            setCategoryMembers([]);
        } finally {
            setFetchingMembers(false);
        }
    };

    const closePrimaFacieAssignModal = () => {
        setIsPrimaFacieAssignOpen(false);
        setPrimaFacieTargetSubmission(null);
        setSelectedMemberIds([]);
        setCategoryMembers([]);
    };

    const handleAssignPrimaFacieMembers = async () => {
        // Strict 3-member panel rule (kept for reference, temporarily relaxed — any 1+ allowed):
        // if (!primaFacieTargetSubmission || selectedMemberIds.length < 3) return;
        if (!primaFacieTargetSubmission || selectedMemberIds.length < 1) return;
        setIsAssigningMembers(true);
        try {
            const assignedMembers = selectedMemberIds.map(id => {
                const member = categoryMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });

            const response = await primaFacieService.assignMembersForPrimaFacie({
                submissionId: primaFacieTargetSubmission.id,
                assignedMembers,
            });
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'Prima Facie panel assigned successfully',
                });
                closePrimaFacieAssignModal();
                fetchSubmissions();
            } else {
                showNotification('error', {
                    title: 'Assignment Failed',
                    message: response.message || 'Failed to assign Prima Facie panel',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred during assignment',
            });
        } finally {
            setIsAssigningMembers(false);
        }
    };

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        setSearch(query);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleViewDetails = (submission: Submission) => {
        setSelectedSubmission(submission);
        setIsDetailsOpen(true);
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    };

    const getStatusBadge = (status: number) => {
        const statusMap: { [key: number]: { label: string; className: string } } = {
            [SubmissionStatus.Submitted]: { label: 'Submitted', className: 'bg-gray-100 text-gray-700' },
            [SubmissionStatus.UnderReview]: { label: 'Under Review', className: 'bg-blue-50 text-blue-700' },
            [SubmissionStatus.Returned]: { label: 'Returned', className: 'bg-yellow-50 text-yellow-700' },
            [SubmissionStatus.Rejected]: { label: 'Rejected', className: 'bg-red-50 text-red-700' },
            [SubmissionStatus.Approved]: { label: 'Approved', className: 'bg-green-50 text-green-700' },
        };
        const config = statusMap[status] ?? statusMap[SubmissionStatus.Submitted];
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${config.className}`}>
                {config.label}
            </span>
        );
    };

    const getApplicationType = (type: number) => (type === 1 ? 'Book' : 'Journal Article');

    // ── Table columns ──────────────────────────────────────────────────────────

    const columns: TableColumn<Submission>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => (
                <span className="text-gray-500 text-sm font-mono">{value}</span>
            ),
        },
        {
            key: 'title',
            label: 'Title / Author',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5">{row.title}</div>
                    <div className="text-sm text-gray-500">
                        {(() => {
                            const authorsStr = row.authors && String(row.authors).trim();
                            if (authorsStr) return authorsStr;
                            const bookAuthors = (row as any).bookAuthors as Array<any> | undefined;
                            if (bookAuthors && bookAuthors.length > 0) {
                                return bookAuthors.map(a => a?.name ?? '').filter(Boolean).join(', ');
                            }
                            return '—';
                        })()}
                    </div>
                    <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {getApplicationType(row.applicationType)}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            key: 'category',
            label: 'Thematic Area',
            render: (value, row) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {row.categoryName || row.category?.name || 'N/A'}
                </span>
            ),
        },
        {
            key: 'submittedAt',
            label: 'Date Submitted',
            render: (value) => (
                <span className="text-sm text-gray-700">{formatDate(value)}</span>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value as number),
        },
        {
            key: 'plagiarismOutcome',
            label: 'Similarity',
            render: (value, row) => (
                <span
                    className={`font-semibold ${row.plagiarismOutcome >= 25
                            ? 'text-red-600'
                            : row.plagiarismOutcome >= 15
                                ? 'text-yellow-600'
                                : 'text-green-600'
                        }`}
                >
                    {row.plagiarismOutcome}%
                </span>
            ),
        },
        {
            key: 'completedPrimaFacie',
            label: 'Prima Facie Completed?',
            render: (value, row) => (
                <div className="flex flex-col items-start gap-1">
                    {row.completedPrimaFacie ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Yes
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                            <XCircle className="w-3.5 h-3.5" />
                            No
                        </span>
                    )}
                    {row.primaFacieDecision && (() => {
                        const dec = row.primaFacieDecision!.toLowerCase();
                        const isRecommended = dec.includes('not') === false;
                        return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                                isRecommended
                                    ? 'bg-green-50 text-green-700 border border-green-100'
                                    : 'bg-red-50 text-red-700 border border-red-100'
                            }`}>
                                {row.primaFacieDecision}
                            </span>
                        );
                    })()}
                </div>
            ),
        },
    ];

    // ── Internal Discussion / SignalR ─────────────────────────────────────────

    useEffect(() => {
        const unsubState = signalRService.onStateChange((status) => {
            setConnectionStatus(status);
        });

        if (isChatOpen && chatSubmission?.id && connectionStatus === ConnectionStatus.Connected) {
            signalRService.joinGroup(chatSubmission.id);
        }

        const unsubscribe = signalRService.onReceiveMessage((data: any) => {
            const msgSubId = data.submissionId || data.SubmissionId;
            const msgId = data.id || data.Id;
            const newMsg: InternalMessage = {
                id: msgId || Date.now().toString(),
                message: data.message || data.Message,
                senderName: data.senderName || data.SenderName || 'System',
                senderUserId: data.senderUserId || data.SenderUserId,
                sendDateTime: data.sendDateTime || data.SendDateTime || new Date().toISOString(),
                receiverName: data.receiverName || data.ReceiverName,
            };

            if (isChatOpen && chatSubmission && msgSubId === chatSubmission.id) {
                setChatMessages(prev => {
                    if (prev.some(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg];
                });
            } else if (msgSubId) {
                setUnreadSubmissionIds(prev => new Set(prev).add(msgSubId));
            }
        });

        return () => {
            if (chatSubmission?.id && connectionStatus === ConnectionStatus.Connected) {
                signalRService.leaveGroup(chatSubmission.id);
            }
            unsubscribe();
            unsubState();
        };
    }, [isChatOpen, chatSubmission?.id, connectionStatus]);

    const fetchRoles = async () => {
        try {
            const rolesRes = await userService.getAdminRoles();
            if (rolesRes.isSuccess && rolesRes.data) {
                const mappedRoles = rolesRes.data.map(r => ({ id: r.id.toString(), name: r.name }));
                let filtered = mappedRoles;
                if (isDESS) {
                    filtered = mappedRoles.filter(r => r.id === '5');
                } else if (isTagChairman) {
                    filtered = mappedRoles.filter(r => r.id === '9');
                } else {
                    filtered = mappedRoles.filter(r => r.id === '9' || r.id === '5');
                }
                setRoles(filtered);
            }
        } catch { /* ignore */ }
    };

    const fetchUsersByRole = async (roleId: string) => {
        setLoadingUsers(true);
        try {
            const res = await userService.getUsersByRoleId(Number(roleId));
            if (res.isSuccess && res.data) {
                setRoleUsers(res.data.map(u => ({
                    id: u.userId,
                    name: u.person?.fullName || u.username,
                })));
            }
        } catch { /* ignore */ } finally {
            setLoadingUsers(false);
        }
    };

    const fetchChatHistory = async (submissionId: string) => {
        setLoadingHistory(true);
        try {
            const res = await communicationService.getMessageHistory(submissionId);
            if (res.isSuccess && res.data) {
                const sorted = [...res.data].sort((a, b) =>
                    new Date(a.sendDateTime).getTime() - new Date(b.sendDateTime).getTime()
                );
                setChatMessages(sorted);
            }
        } catch { /* ignore */ } finally {
            setLoadingHistory(false);
        }
    };

    const handleOpenChat = async (submission: Submission) => {
        if (connectionStatus !== ConnectionStatus.Connected) {
            showNotification('error', {
                title: 'Offline',
                message: 'You cannot open the chat interface while offline. Please check your internet connection or wait for the system to reconnect.',
            });
            return;
        }

        setChatSubmission(submission);
        setIsChatOpen(true);
        setUnreadSubmissionIds(prev => {
            const next = new Set(prev);
            next.delete(submission.id);
            return next;
        });

        try {
            await communicationService.markAsRead(submission.id);
            if (refreshUnread) await refreshUnread();
        } catch { /* ignore */ }

        fetchChatHistory(submission.id);
    };

    const handleCloseChat = () => {
        setIsChatOpen(false);
        setChatMessages([]);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedRecipients.length === 0 || !chatSubmission || !composeMessage.trim()) return;

        setSendingMessage(true);
        try {
            const res = await communicationService.sendMessage({
                submissionId: chatSubmission.id,
                receiverId: selectedRecipients.join(','),
                message: composeMessage.trim(),
            });
            if (res.isSuccess) {
                setIsComposeModalOpen(false);
                setComposeMessage('');
                setSelectedRecipients([]);
                setSelectedRoleId('');
                showNotification('success', { title: 'Message Sent', message: 'Your message has been delivered.' });
            }
        } catch {
            showNotification('error', { title: 'Send Failed', message: 'Could not deliver your message.' });
        } finally {
            setSendingMessage(false);
        }
    };

    useEffect(() => {
        if (isDESSOrTagChairman) fetchRoles();
    }, [isDESSOrTagChairman]);

    // Deep link: ?openChat=<submissionId>
    useEffect(() => {
        if (!initialized || submissions.length === 0) return;
        const params = new URLSearchParams(location.search);
        const openChatId = params.get('openChat');
        if (openChatId) {
            const target = submissions.find(s => s.id === openChatId);
            if (target) {
                handleOpenChat(target);
                window.history.replaceState({}, '', location.pathname);
            }
        }
    }, [location.search, submissions, initialized]);

    const actions: TableAction<Submission>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Details',
                    onClick: handleViewDetails,
                    className: 'text-blue-600',
                },
                {
                    label: 'Move Submission',
                    onClick: openMoveModal,
                    className: (row: Submission) => {
                        if (isCategoryAdmin) return 'hidden';
                        if (isDESS) return 'hidden';
                        if (isTagChairman && isReadyForReviewerAssignment(row)) return 'hidden';
                        return 'text-blue-600';
                    },
                },
                {
                    label: 'Assign Reviewer',
                    onClick: () => navigate('/admin/reviewer-assignment'),
                    className: (row: Submission) =>
                        isTagChairman && isReadyForReviewerAssignment(row)
                            ? 'text-[#088A61] font-semibold'
                            : 'hidden',
                },
                {
                    label: 'Assign Prima Facie Panel',
                    onClick: openPrimaFacieAssignModal,
                    className: (row: Submission) =>
                        isCategoryAdmin && !row.completedPrimaFacie ? 'text-blue-600' : 'hidden',
                },
                {
                    label: 'Move submission to Author',
                    onClick: (row: Submission) => openChangeStatusModal(row),
                    className: (row: Submission) => {
                        if (isTagChairman && isAuthorMoveDecision(row.finalDecision)) return 'text-[#088A61] font-semibold';
                        if (isDESS && (isAuthorMoveDecision(row.finalDecision) || row.primaFacieDecision === 'NotRecommended')) return 'text-[#088A61] font-semibold';
                        return 'hidden';
                    },
                },
                {
                    label: 'Discuss (Internal)',
                    onClick: (row: Submission) => handleOpenChat(row),
                    className: () => (isDESSOrTagChairman ? 'text-green-600 font-semibold' : 'hidden'),
                },
            ],
            title: 'Actions',
        },
    ];

    const openChangeStatusModal = (submission: Submission) => {
        setStatusTarget(submission);
        setStatusComment('');
        setIsChangeStatusModalOpen(true);

        // Pre-set status if prima facie was NotRecommended and there's no finalDecision to derive from
        if (!isAuthorMoveDecision(submission.finalDecision) && submission.primaFacieDecision === 'NotRecommended') {
            setNewStatus(SubmissionStatus.Rejected);
        } else {
            setNewStatus('');
        }
    };

    const closeChangeStatusModal = () => {
        setIsChangeStatusModalOpen(false);
        setStatusTarget(null);
        setNewStatus('');
        setStatusComment('');
    };

    const handleChangeStatus = async () => {
        if (!statusTarget || newStatus === '') return;
        setIsUpdatingStatus(true);
        try {
            const payload = {
                submissionId: statusTarget.id,
                comment: statusComment.trim() || '',
                submissionStatus: newStatus as number,
            };
            const response = await submissionService.communicateSubmissionStatusToAuthor(payload);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: response.message || 'Submission moved to author successfully.' });
                closeChangeStatusModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to move submission to author.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while updating status.' });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        {deskRoleLabel ? `Submissions by ${deskRoleLabel}'s Desk` : 'Submissions by Desk'}
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        View manuscripts currently at your desk for review and processing.
                    </p>
                </div>
                {/* <ExportButton
                    reportType={2}
                    parameters={{
                        sortBy: 'SubmittedAt',
                        sortDescending: true,
                        search,
                    }}
                    reportTitle="Desk Submissions Report"
                    buttonText="Export Submissions"
                /> */}
            </div>

            {/* Table */}
            <ReusableTable
                title="Desk Submissions"
                subTitle="Manuscripts currently assigned to your desk"
                columns={columns}
                data={submissions}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, author, or ID..."
                onSearch={handleSearch}
                emptyMessage="No submissions found at your desk."
                showSearch={true}
                showFilter={false}
            />

            {/* Pagination */}
            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* ── Change Submission Status Modal (DESS only) ── */}
            <Modal
                isOpen={isChangeStatusModalOpen}
                onClose={closeChangeStatusModal}
                title="Communicate Submission Status"
                description="Move the submission back to the author with an optional comment."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {statusTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {statusTarget.title ?? '—'}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">
                                {statusTarget.submissionNumber ?? '—'}
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Submission Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={newStatus}
                            onChange={(e) => {
                                const val = e.target.value === '' ? '' : Number(e.target.value);
                                setNewStatus(val);
                            }}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        >
                            <option value="">Select Status...</option>
                            {(() => {
                                const dec = statusTarget?.finalDecision;
                                if (isAuthorMoveDecision(dec)) {
                                    const opt = statusForAuthorMoveDecision(dec);
                                    return <option value={opt.value}>{opt.label}</option>;
                                }
                                if (statusTarget?.primaFacieDecision === 'NotRecommended') {
                                    return <option value={SubmissionStatus.Rejected}>Rejected</option>;
                                }
                                return null;
                            })()}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={statusComment}
                            onChange={(e) => setStatusComment(e.target.value)}
                            placeholder="Add a comment regarding this status change..."
                            rows={4}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />

                        {/* Removed 'Send reviewed manuscript to author' toggle — system handles file sending */}
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeChangeStatusModal}
                            disabled={isUpdatingStatus}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleChangeStatus}
                            disabled={newStatus === '' || isUpdatingStatus}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isUpdatingStatus ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Moving...
                                </>
                            ) : (
                                <>
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Move
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Move Submission Modal */}
            <Modal
                isOpen={isMoveModalOpen}
                onClose={closeMoveModal}
                title="Move Submission"
                description="You're about to move this submission to another desk. Please confirm the destination and optionally leave a comment before proceeding."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {moveSubmission && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Submission
                            </p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {moveSubmission.title}
                            </p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Move To <span className="text-red-500">*</span>
                        </label>
                        {deskListLoading ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading desks...
                            </div>
                        ) : (
                            <select
                                value={selectedDeskId}
                                onChange={(e) => setSelectedDeskId(e.target.value === '' ? '' : Number(e.target.value))}
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            >
                                <option value="">Select destination desk...</option>
                                {deskList.map((desk) => (
                                    <option key={desk.id} value={desk.id}>
                                        {desk.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={moveComment}
                            onChange={(e) => setMoveComment(e.target.value)}
                            placeholder="Add a note about this move..."
                            rows={3}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeMoveModal}
                            disabled={isMoving}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleMoveSubmission}
                            disabled={isMoving || selectedDeskId === ''}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isMoving ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Moving...
                                </>
                            ) : (
                                <>
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Move Submission
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Assign Prima Facie Panel Modal */}
            <Modal
                isOpen={isPrimaFacieAssignOpen}
                onClose={closePrimaFacieAssignModal}
                title="Assign Prima Facie Panel"
                description="Select one or more thematic area members to form the Prima Facie review panel for this submission."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {primaFacieTargetSubmission && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{primaFacieTargetSubmission.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{primaFacieTargetSubmission.category?.name}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Panel Members <span className="text-red-500">*</span>
                            <span className="text-gray-400 font-normal ml-1">(select 1 or more)</span>
                        </label>
                        {fetchingMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading members...
                            </div>
                        ) : categoryMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No members available for this thematic area
                            </div>
                        ) : (
                            <MultiSelect
                                options={categoryMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedMemberIds}
                                onChange={setSelectedMemberIds}
                                placeholder="Select panel members..."
                            />
                        )}
                        {selectedMemberIds.length > 0 && (
                            <>
                                {/* Strict 3-member hint (kept for reference, temporarily relaxed):
                                <p className={`text-xs mt-1.5 font-medium ${selectedMemberIds.length >= 3 ? 'text-green-700' : 'text-amber-600'}`}>
                                    {selectedMemberIds.length} selected
                                    {selectedMemberIds.length < 3 && ` — select at least ${3 - selectedMemberIds.length} more`}
                                </p> */}
                                <p className="text-xs mt-1.5 font-medium text-green-700">
                                    {selectedMemberIds.length} selected
                                </p>
                            </>
                        )}
                    </div>

                    {/* Selected members preview */}
                    {selectedMemberIds.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1.5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected Panel</p>
                            {selectedMemberIds.map(id => {
                                const member = categoryMembers.find(m => m.id === id);
                                return member ? (
                                    <div key={String(id)} className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                                            {member.name.charAt(0)}
                                        </div>
                                        <span className="text-sm text-gray-700">{member.name}</span>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closePrimaFacieAssignModal}
                            disabled={isAssigningMembers}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAssignPrimaFacieMembers}
                            disabled={isAssigningMembers || selectedMemberIds.length === 0 /* was: < 3 (strict 3-member panel) */}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isAssigningMembers ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Assigning...
                                </>
                            ) : (
                                <>
                                    <Users className="w-4 h-4" />
                                    Assign Panel
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Details Drawer */}
            <Drawer
                isOpen={isDetailsOpen}
                onClose={() => {
                    setIsDetailsOpen(false);
                    setSelectedSubmission(null);
                }}
                title="Submission Details"
                width="max-w-3xl"
            >
                {selectedSubmission && (
                    <AdminSubmissionDetails submissionId={selectedSubmission.id} />
                )}
            </Drawer>

            {/* ── Internal Discussion Chat Drawer (DESS / TAG Chairman) ── */}
            <Drawer
                isOpen={isChatOpen}
                onClose={handleCloseChat}
                title={`Discussion: ${chatSubmission?.title ?? ''}`}
                width="max-w-xl"
            >
                <div className="flex flex-col h-full -m-6">
                    <div className="bg-gradient-to-r from-[#088A61]/10 to-emerald-50 border-b border-[#088A61]/20 px-5 py-3.5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold text-[#088A61] uppercase tracking-widest mb-0.5">Submission</p>
                                <p className="text-sm font-bold text-gray-900 line-clamp-1">{chatSubmission?.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{chatSubmission?.author?.person?.fullName ?? '—'}</p>
                            </div>
                            <div className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${connectionStatus === ConnectionStatus.Connected
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : connectionStatus === ConnectionStatus.Reconnecting
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-200 animate-pulse'
                                    : 'bg-red-50 text-red-700 border-red-200 animate-pulse'
                                }`}>
                                {connectionStatus === ConnectionStatus.Connected ? <Wifi size={10} /> : <WifiOff size={10} />}
                                {connectionStatus === ConnectionStatus.Connected ? ''
                                    : connectionStatus === ConnectionStatus.Reconnecting ? 'Reconnecting'
                                        : 'Connecting'}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-gray-50">
                        {loadingHistory ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#088A61]"></div>
                                <p className="text-sm text-gray-500 font-medium">Loading messages...</p>
                            </div>
                        ) : chatMessages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-3">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                    <MessageSquare size={28} className="text-gray-300" />
                                </div>
                                <p className="text-sm font-semibold text-gray-400">No messages yet</p>
                                <p className="text-xs text-gray-400">Start a discussion using the button below.</p>
                            </div>
                        ) : (
                            chatMessages.map((msg) => {
                                const myName = user?.name?.toLowerCase().trim() || '';
                                const senderName = msg.senderName?.toLowerCase().trim() || '';
                                const isMine = (msg.senderUserId && msg.senderUserId === user?.id) ||
                                    (senderName === myName && myName !== '');
                                return (
                                    <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                                        <div className={`flex items-center gap-1.5 mb-1 px-1 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 bg-gray-400">
                                                {msg.senderName?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <span className="text-[11px] font-semibold text-gray-600">
                                                {isMine ? '' : msg.senderName}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                                {new Date(msg.sendDateTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div className={`max-w-[80%] px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed whitespace-pre-wrap ${isMine
                                            ? 'bg-[#088A61] text-white rounded-tr-sm'
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
                                            }`}>
                                            {msg.message}
                                        </div>
                                        {msg.receiverName && (
                                            <div className="mt-1 px-1 flex items-center gap-1">
                                                <span className={`text-[10px] font-medium ${isMine ? 'text-[#088A61]/70' : 'text-gray-400'}`}>
                                                    To: {msg.receiverName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="p-4 bg-white border-t border-gray-100 flex justify-center">
                        <button
                            onClick={() => setIsComposeModalOpen(true)}
                            className="flex items-center gap-2 bg-[#088A61] text-white px-7 py-3 rounded-full text-sm font-bold shadow-lg shadow-[#088A61]/20 hover:bg-[#077a55] hover:scale-105 active:scale-95 transition-all"
                        >
                            <PlusCircle size={18} />
                            New Message
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* ── Compose Internal Message Modal ── */}
            {isComposeModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg relative z-10 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="text-lg font-bold text-gray-900 truncate pr-4">
                                Internal Discussion: <span className="text-[#088A61]">{chatSubmission?.title}</span>
                            </h3>
                            <button onClick={() => setIsComposeModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select Role:</label>
                                    <select
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#088A61] outline-none"
                                        value={selectedRoleId}
                                        onChange={(e) => {
                                            const roleId = e.target.value;
                                            setSelectedRoleId(roleId);
                                            setSelectedRecipients([]);
                                            if (roleId) fetchUsersByRole(roleId);
                                            else setRoleUsers([]);
                                        }}
                                    >
                                        <option value="">Select a Role</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedRoleId && (
                                    <div className="relative">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select User(s):</label>
                                        <div
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm cursor-pointer flex justify-between items-center"
                                            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                        >
                                            <span className="truncate text-gray-700 font-medium">
                                                {loadingUsers ? 'Loading...' : selectedRecipients.length === 0 ? 'Select Recipient' : roleUsers.find(u => u.id === selectedRecipients[0])?.name || 'User Selected'}
                                            </span>
                                            <ChevronDown size={16} className={`text-gray-400 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                                        </div>

                                        {isUserDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setIsUserDropdownOpen(false)}></div>
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 max-h-60 overflow-y-auto p-2">
                                                    {roleUsers.map(u => (
                                                        <label key={u.id} className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-xl mb-1 transition-colors ${selectedRecipients.includes(u.id) ? 'bg-[#088A61]/5 border border-[#088A61]/20' : ''}`}>
                                                            <input
                                                                type="radio" name="composeUser" className="w-4 h-4 border-gray-300 text-[#088A61] focus:ring-[#088A61]"
                                                                checked={selectedRecipients.includes(u.id)}
                                                                onChange={() => {
                                                                    setSelectedRecipients([u.id]);
                                                                    setIsUserDropdownOpen(false);
                                                                }}
                                                            />
                                                            <span className={`text-sm ${selectedRecipients.includes(u.id) ? 'font-bold text-[#088A61]' : 'text-gray-700 font-medium'}`}>{u.name}</span>
                                                        </label>
                                                    ))}
                                                    {roleUsers.length === 0 && !loadingUsers && <p className="p-3 text-xs text-gray-400 italic text-center">No users found</p>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Compose Message:</label>
                                <textarea
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-[#088A61] outline-none min-h-[150px] resize-none"
                                    placeholder="Type your internal message here..."
                                    value={composeMessage}
                                    onChange={(e) => setComposeMessage(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button onClick={() => setIsComposeModalOpen(false)} className="px-6 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200">Cancel</button>
                            <button
                                onClick={handleSendMessage}
                                disabled={selectedRecipients.length === 0 || sendingMessage || !composeMessage.trim()}
                                className={`px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center gap-2 ${selectedRecipients.length > 0 && !sendingMessage && composeMessage.trim()
                                    ? 'bg-[#088A61] text-white hover:scale-105 active:scale-95'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                {sendingMessage ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <SendHorizontal size={18} />
                                )}
                                {sendingMessage ? 'Sending...' : 'Send Message'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SubmissionsByDesk;